import { ButtonTertiary, ButtonPrimary } from "components/Atoms/Button";
import classNames from "classnames";
import React from "react";
import { useCustomQuery } from "utilities/hookutils";
import { useMutation } from "@tanstack/react-query";
import { SelectSenderSignerModal } from "eSign/components/Participant/SelectSenderSignerModal";
import { ContactParticipant } from "components/Picker/ContactParticipantPicker/ContactParticipantModalPicker";
import { useCurrentUser } from "common/SessionContext";

interface InternalUserOrContactProps {
  onInternalUserClick: () => void;
  onContactClick: () => void;
  isLoadingInternalUser: boolean;
  isLoadingContact: boolean;
  noSenderError: boolean;
}

const InternalUserOrContact = (props: InternalUserOrContactProps) => {
  const classes = classNames("flex gap-large", {
    "ui message red": props.noSenderError
  });
  const isLoadingAny = props.isLoadingInternalUser || props.isLoadingContact;

  return <>
    <div className={classes}>
      <div>
        <ButtonTertiary
          label={RESX.UserType.InternalUser}
          onClick={props.onInternalUserClick}
          loading={props.isLoadingInternalUser}
          disabled={isLoadingAny}
        />
      </div>
      <div>
        <ButtonPrimary
          label={RESX.Contact.resxContact}
          onClick={props.onContactClick}
          loading={props.isLoadingContact}
          disabled={isLoadingAny}
        />
      </div>
    </div>
  </>;
}

interface Props {
  bookkeeping: server.dto.BookkeepingSmall;
  esignDocument: server.dto.ESignDocument;
  onSenderChange: (sender: server.dto.ESignParticipant) => void;
  role: server.dto.ESignParticipantRoles;
  noSenderError: boolean;
}
export const InternalUserOrContactDetailSection = (props: Props) => {
  const currentUser = useCurrentUser();
  const [displaySelectContactModal, setDisplaySelectContactModal] = React.useState(false);

  const bookkeepingCompanyQuery = useCustomQuery({
    //TODO: This can actually have errors we ain't trapping
    queryFn: async () => window.CoreApi.Bookkeeping.GetSidePanelData(props.bookkeeping.bookkeepingId).then(b => b.data.data.bookkeeping.company),
    queryKey: ["getBookkeeping", props.bookkeeping]
  });

  const setSenderFromUserMutation = useMutation(window.CoreApi.ESign.SetSenderFromUser);
  const setParticipantFromContactMutation = useMutation(window.CoreApi.ESign.SetParticipantFromContact);

  const onInternalUserClick = async () => {
    let bookkeepingCompany: server.dto.CompanySmall;
    if (bookkeepingCompanyQuery.isFetching) {
      bookkeepingCompany = await bookkeepingCompanyQuery.refetch().then(r => r.data);
    } else {
      bookkeepingCompany = bookkeepingCompanyQuery.data;
    }

    const result = await setSenderFromUserMutation.mutateAsync({
      userId: currentUser.userId,
      companyId: bookkeepingCompany.companyId,
      documentId: props.esignDocument.documentId,
    });
    if (!result.data.hasErrors) {
      props.onSenderChange(result.data.data);
    } else {
      window.displayGenericModal("Error", result.data.errors[0].message, "tiny", "red");
    }
  }

  const onContactSelected = async (participant: ContactParticipant) => {
    setDisplaySelectContactModal(false);

    const result = await setParticipantFromContactMutation.mutateAsync({
      documentId: props.esignDocument.documentId,
      role: props.role,
      contactId: participant.contact.contactId,
      emailAddress: participant.email,
      companyId: participant.company.companyId,
      contactCulture: participant.culture.lcid
    });

    if (!result.data.hasErrors) {
      props.onSenderChange(result.data.data);
    } else {
      window.displayGenericModal("Error", result.data.errors[0].message, "tiny", "red");
    }
  }

  return <div style={{ padding: 8 }}>
    <InternalUserOrContact
      onInternalUserClick={onInternalUserClick}
      onContactClick={() => setDisplaySelectContactModal(true)}
      isLoadingInternalUser={setSenderFromUserMutation.isLoading}
      isLoadingContact={setParticipantFromContactMutation.isLoading}
      noSenderError={props.noSenderError}
    />
    {displaySelectContactModal && <SelectSenderSignerModal
      startOpen
      documentId={props.esignDocument.documentId}
      documentType={props.esignDocument.documentType}
      onChange={onContactSelected}
      onClose={() => setDisplaySelectContactModal(false)}
      hasError={props.noSenderError}
      senderOrSigner={"sender"}
    />}
  </div>
}
